import { VisualizationTypeSchema } from '@aibi/contracts';
import {
  Badge,
  Button,
  Dialog,
  DialogDescription,
  DialogTitle,
  EmptyState,
  Input,
  SheetContent,
  Spinner,
} from '@aibi/ui';
import { Send, Sparkles } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type SyntheticEvent,
} from 'react';
import { useServices } from '../../../app/services';
import { sameContent } from '../../model/workspace-document';
import type {
  AssistantCandidate,
  AssistantConnection,
  AssistantReply,
} from '../../services/assistant-port';
import { useSelectedCard } from '../../state/use-selected-card';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { AiReplyView, type ProposalStatus } from './AiReplyView';

type Message =
  | { readonly id: number; readonly role: 'user' | 'error'; readonly text: string }
  | {
      readonly id: number;
      readonly role: 'reply';
      readonly reply: AssistantReply;
      readonly status: ProposalStatus;
    };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

const SUGGESTIONS = [
  'Muestrame las ventas por region',
  'Top 5 paises por ventas en barras',
  'Por que veo este resultado?',
] as const;

const BUBBLE: Readonly<Record<Message['role'], string>> = {
  user: 'ml-8 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground',
  error:
    'mr-8 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive',
  reply: 'mr-8 rounded-lg bg-muted px-3 py-2 text-sm',
};

const noUnsubscribe = () => undefined;

/** Estado del proveedor: solo se muestra "conectado" tras una llamada real exitosa. */
function ConnectionBadge({
  connection,
  providerName,
  available,
}: {
  readonly connection: AssistantConnection;
  readonly providerName: string | null;
  readonly available: boolean;
}) {
  if (!available || connection === 'unconfigured') {
    return <Badge variant="warning">Sin proveedor</Badge>;
  }
  const name = providerName ?? 'Asistente';
  switch (connection) {
    case 'unverified':
      return <Badge variant="outline">{`${name} (sin verificar)`}</Badge>;
    case 'connected':
      return <Badge variant="success">{name}</Badge>;
    case 'failing':
      return <Badge variant="warning">{`${name} (con errores)`}</Badge>;
    case 'local':
      return <Badge variant="outline">{name}</Badge>;
  }
}

/**
 * Panel del asistente. Depende solo del puerto `AssistantPort` (sin llamadas de red
 * propias). El contexto enviado contiene solo el AnalysisState seleccionado (intencion),
 * nunca datos ni credenciales. Las propuestas se aplican solo con un clic explicito, como
 * un unico paso deshacible del historial.
 */
export function AiPanel() {
  const { aiPanelOpen } = useUiState();
  const uiDispatch = useUiDispatch();
  const { assistant } = useServices();
  const { document } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const selectedCard = useSelectedCard();
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [prompt, setPrompt] = useState('');
  const [pending, setPending] = useState(false);
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const subscribe = useCallback(
    (listener: () => void) => assistant.subscribe?.(listener) ?? noUnsubscribe,
    [assistant],
  );
  const connection = useSyncExternalStore(subscribe, () => assistant.connection);

  useEffect(() => () => controller.current?.abort(), []);

  const append = (message: DistributiveOmit<Message, 'id'>) => {
    sequence.current += 1;
    const id = sequence.current;
    setMessages((current) => [...current, { ...message, id }]);
  };

  const setStatus = (id: number, status: ProposalStatus) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id && message.role === 'reply' ? { ...message, status } : message,
      ),
    );
  };

  const ask = async (text: string) => {
    if (text === '' || pending) return;
    append({ role: 'user', text });
    setPrompt('');
    setPending(true);
    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;
    const reply = await assistant.ask(
      {
        prompt: text,
        context: { semanticModelId: document.semanticModelId, analysisState: selectedCard },
      },
      current.signal,
    );
    if (current.signal.aborted) return;
    setPending(false);
    if (reply.ok) append({ role: 'reply', reply: reply.value, status: 'pending' });
    else append({ role: 'error', text: reply.error.message });
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    void ask(prompt.trim());
  };

  const apply = (message: Extract<Message, { role: 'reply' }>) => {
    const { reply } = message;
    if (reply.kind === 'proposal' && reply.state !== null) {
      const cardId = reply.baseState?.id ?? null;
      const current = cardId === null ? null : document.cards.find((card) => card.id === cardId);
      if (cardId !== null && (!current || !sameContent(current, reply.baseState))) {
        append({
          role: 'error',
          text: 'La visualizacion cambio desde que se genero la propuesta. Vuelva a preguntar al asistente.',
        });
        return;
      }
      execute({
        type: 'apply-ai-proposal',
        cardId,
        baseState: reply.baseState,
        state: reply.state,
      });
      setStatus(message.id, 'applied');
    } else if (reply.kind === 'suggestion' && reply.cardId !== null) {
      execute({
        type: 'set-visualization-type',
        cardId: reply.cardId,
        visualization: reply.visualization,
      });
      setStatus(message.id, 'applied');
    }
  };

  const choose = (candidate: AssistantCandidate) => {
    if (candidate.followUp !== null) {
      void ask(candidate.followUp);
      return;
    }
    if (candidate.kind === 'filter' && selectedCard) {
      execute({ type: 'remove-filter', cardId: selectedCard.id, filterId: candidate.id });
      return;
    }
    const visualization = VisualizationTypeSchema.safeParse(candidate.id);
    if (candidate.kind === 'visualization' && selectedCard && visualization.success) {
      execute({
        type: 'set-visualization-type',
        cardId: selectedCard.id,
        visualization: visualization.data,
      });
      return;
    }
    setPrompt(candidate.label);
  };

  return (
    <Dialog
      modal={false}
      open={aiPanelOpen}
      onOpenChange={(open) => {
        uiDispatch({ type: 'set-ai-panel', open });
      }}
    >
      <SheetContent
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <header className="space-y-1.5 border-b border-border px-4 py-3 pr-10">
          <div className="flex items-center gap-2">
            <Sparkles aria-hidden className="size-4 text-primary" />
            <DialogTitle>Asistente de analisis</DialogTitle>
            <ConnectionBadge
              connection={connection}
              providerName={assistant.providerName}
              available={assistant.available}
            />
          </div>
          <DialogDescription>
            {assistant.available
              ? 'Pida cambios en lenguaje natural. Nada se aplica sin su confirmacion.'
              : 'No hay un proveedor de IA configurado. La interfaz esta lista para conectarse en una proxima version.'}
          </DialogDescription>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" aria-live="polite">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <EmptyState
                icon={<Sparkles />}
                title="Sin conversaciones todavia"
                description={
                  selectedCard
                    ? 'El asistente usara como contexto la visualizacion seleccionada.'
                    : 'Sin visualizacion seleccionada, las propuestas crean una visualizacion nueva.'
                }
              />
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Ejemplos de preguntas</p>
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="block w-full rounded-md border border-border px-3 py-2 text-left text-xs hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    onClick={() => {
                      setPrompt(suggestion);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ol className="space-y-2" aria-label="Conversacion">
              {messages.map((message) => (
                <li key={message.id} className={BUBBLE[message.role]}>
                  {message.role === 'reply' ? (
                    <AiReplyView
                      reply={message.reply}
                      status={message.status}
                      canApplySuggestion={
                        message.reply.kind === 'suggestion' && message.reply.cardId !== null
                      }
                      onApply={() => {
                        apply(message);
                      }}
                      onDismiss={() => {
                        setStatus(message.id, 'dismissed');
                      }}
                      onCandidate={choose}
                    />
                  ) : (
                    message.text
                  )}
                </li>
              ))}
              {pending && (
                <li className="mr-8">
                  <Spinner label="El asistente esta respondiendo" />
                </li>
              )}
            </ol>
          )}
        </div>
        <form
          className="flex items-center gap-2 border-t border-border px-4 py-3"
          onSubmit={submit}
        >
          <Input
            aria-label="Pregunta para el asistente"
            placeholder="Escriba una pregunta..."
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
            }}
          />
          <Button
            type="submit"
            size="sm"
            disabled={prompt.trim() === '' || pending}
            aria-label="Enviar pregunta"
          >
            <Send />
          </Button>
        </form>
      </SheetContent>
    </Dialog>
  );
}
